import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useApp } from '../context/AppContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Package, PackageX, TrendingUp, TrendingDown, Receipt, ArrowRight, BarChart3 } from 'lucide-react';
import { Button } from '../components/ui/button';
import ArcadeMark from '../components/ArcadeMark';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

// Palette arcade (rétro-futur 80s) pour les graphes
const ARCADE = {
  amber: '#FFB020',
  magenta: '#FF2E88',
  cyan: '#28D6F5',
  violet: '#A855F7',
  green: '#2FE0A0',
};

const Dashboard = () => {
  const { t, API } = useApp();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(value || 0);
  const formatDate = (dateString) => new Date(dateString).toLocaleDateString();

  // Les types de bornes sont des valeurs dynamiques : on traduit si une clé fixe existe, sinon valeur brute.
  const translateCabinetType = (type) => {
    if (!type || type === 'null') return t('dashboard.noData');
    const key = `cabinetTypes.${type}`;
    const translated = t(key);
    return translated === key ? type : translated;
  };

  const getOperationColor = (type) => {
    switch (type) {
      case 'purchase': return 'bg-primary/15 text-primary border-primary/30';
      case 'sale': return 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30';
      case 'purchase_system': return 'bg-violet-500/15 text-violet-400 border-violet-500/30';
      case 'repair': return 'bg-secondary/15 text-secondary border-secondary/30';
      default: return 'bg-muted text-muted-foreground border-border';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in-up" data-testid="dashboard-page">
      {/* Header */}
      <div>
        <p className="eyebrow text-[11px] text-primary/80 mb-2">Arcade Fleet · Stock Manager</p>
        <h1 className="font-display text-4xl md:text-5xl font-bold tracking-[0.08em] uppercase sunset-text">
          {t('dashboard.title')}
        </h1>
        <p className="text-muted-foreground mt-2 font-mono text-xs">
          {new Date().toLocaleDateString()} <span className="text-secondary">◆</span> Player&nbsp;1 · Credits ready
        </p>
      </div>

      {/* KPI readouts — Bento */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        {/* Total Cabinets — hero readout */}
        <Card className="md:col-span-2 lg:col-span-2 bezel relative overflow-hidden border-primary/25" data-testid="stat-total-cabinets">
          <div className="absolute top-0 inset-x-0 h-0.5 bg-gradient-to-r from-primary via-secondary to-transparent" />
          <CardHeader className="pb-2">
            <CardTitle className="eyebrow text-[10px] text-muted-foreground">{t('dashboard.totalCabinets')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-4 min-w-0">
                <div className="w-16 h-16 rounded-md bg-primary/10 flex items-center justify-center glow-amber shrink-0">
                  <ArcadeMark className="w-9 h-9 text-primary" />
                </div>
                <span className="font-display text-5xl md:text-6xl font-bold text-primary text-glow-amber tabular-nums">{stats?.total_cabinets || 0}</span>
              </div>
              <Button variant="ghost" size="icon" className="shrink-0" onClick={() => navigate('/cabinets')} data-testid="go-to-cabinets-btn">
                <ArrowRight className="w-5 h-5" />
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* In Stock */}
        <Card className="bezel relative overflow-hidden border-emerald-500/20" data-testid="stat-in-stock">
          <div className="absolute top-0 inset-x-0 h-0.5 bg-emerald-500/60" />
          <CardHeader className="pb-2">
            <CardTitle className="eyebrow text-[10px] text-muted-foreground">{t('dashboard.inStock')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-12 h-12 rounded-md bg-emerald-500/10 flex items-center justify-center shrink-0">
                <Package className="w-6 h-6 text-emerald-400" />
              </div>
              <span className="font-display text-4xl font-bold text-emerald-400 tabular-nums">{stats?.in_stock || 0}</span>
            </div>
          </CardContent>
        </Card>

        {/* Out of Stock */}
        <Card className="bezel relative overflow-hidden border-secondary/20" data-testid="stat-out-of-stock">
          <div className="absolute top-0 inset-x-0 h-0.5 bg-secondary/60" />
          <CardHeader className="pb-2">
            <CardTitle className="eyebrow text-[10px] text-muted-foreground">{t('dashboard.outOfStock')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-12 h-12 rounded-md bg-secondary/10 flex items-center justify-center shrink-0">
                <PackageX className="w-6 h-6 text-secondary" />
              </div>
              <span className="font-display text-4xl font-bold text-secondary text-glow-magenta tabular-nums">{stats?.out_of_stock || 0}</span>
            </div>
          </CardContent>
        </Card>

        {/* Total Investment */}
        <Card className="bezel relative overflow-hidden" data-testid="stat-investment">
          <div className="absolute top-0 inset-x-0 h-0.5 bg-primary/50" />
          <CardHeader className="pb-2">
            <CardTitle className="eyebrow text-[10px] text-muted-foreground">{t('dashboard.totalInvestment')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-12 h-12 rounded-md bg-primary/10 flex items-center justify-center shrink-0">
                <TrendingDown className="w-6 h-6 text-primary" />
              </div>
              <span className="font-display text-xl md:text-2xl font-bold text-primary tabular-nums truncate">{formatCurrency(stats?.total_purchases)}</span>
            </div>
          </CardContent>
        </Card>

        {/* Total Revenue */}
        <Card className="bezel relative overflow-hidden" data-testid="stat-revenue">
          <div className="absolute top-0 inset-x-0 h-0.5 bg-emerald-500/50" />
          <CardHeader className="pb-2">
            <CardTitle className="eyebrow text-[10px] text-muted-foreground">{t('dashboard.totalRevenue')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-12 h-12 rounded-md bg-emerald-500/10 flex items-center justify-center shrink-0">
                <TrendingUp className="w-6 h-6 text-emerald-400" />
              </div>
              <span className="font-display text-xl md:text-2xl font-bold text-emerald-400 tabular-nums truncate">{formatCurrency(stats?.total_sales)}</span>
            </div>
          </CardContent>
        </Card>

        {/* Cabinets by Type */}
        <Card className="md:col-span-2 bezel" data-testid="stat-by-type">
          <CardHeader>
            <CardTitle className="eyebrow text-[10px] text-muted-foreground">{t('dashboard.cabinetsByType')}</CardTitle>
          </CardHeader>
          <CardContent>
            {stats?.cabinets_by_type && Object.keys(stats.cabinets_by_type).length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {Object.entries(stats.cabinets_by_type).map(([type, count]) => {
                  const isReal = type && type !== 'null';
                  return (
                    <Badge
                      key={type}
                      variant="outline"
                      role={isReal ? 'button' : undefined}
                      onClick={isReal ? () => navigate(`/cabinets?type=${encodeURIComponent(type)}`) : undefined}
                      className={`font-mono text-xs px-3 py-1.5 border-border bg-muted/40 ${isReal ? 'cursor-pointer hover:border-primary hover:text-primary hover:bg-primary/10 transition-colors' : ''}`}
                    >
                      {translateCabinetType(type)}<span className="ml-1.5 font-bold text-primary">{count}</span>
                    </Badge>
                  );
                })}
              </div>
            ) : (
              <p className="text-muted-foreground text-sm">{t('dashboard.noData')}</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Monthly activity */}
      <Card className="bezel">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 font-heading">
            <BarChart3 className="h-5 w-5 text-primary" /> {t('dashboard.monthlyActivity')}
          </CardTitle>
        </CardHeader>
        <CardContent className="pl-2">
          {stats.monthly_chart && stats.monthly_chart.length > 0 ? (
            <div className="h-[300px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stats.monthly_chart} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.12} />
                  <XAxis dataKey="name" tick={{ fontSize: 11, fontFamily: 'JetBrains Mono' }} />
                  <YAxis tick={{ fontSize: 11, fontFamily: 'JetBrains Mono' }} />
                  <Tooltip
                    formatter={(value) => formatCurrency(value)}
                    contentStyle={{ backgroundColor: 'hsl(var(--popover))', borderColor: 'hsl(var(--border))', color: 'hsl(var(--popover-foreground))', borderRadius: '0.5rem', fontFamily: 'JetBrains Mono', fontSize: 12 }}
                    cursor={{ fill: 'hsl(var(--primary) / 0.06)' }}
                  />
                  <Legend wrapperStyle={{ fontSize: 12, fontFamily: 'JetBrains Mono' }} />
                  <Bar dataKey="purchase" name={t('dashboard.lblPurchases')} fill={ARCADE.amber} radius={[4, 4, 0, 0]} />
                  <Bar dataKey="sale" name={t('dashboard.lblSales')} fill={ARCADE.green} radius={[4, 4, 0, 0]} />
                  <Bar dataKey="repair" name={t('dashboard.lblRepairs')} fill={ARCADE.magenta} radius={[4, 4, 0, 0]} />
                  <Bar dataKey="purchase_system" name={t('dashboard.lblSystems')} stackId="a" fill={ARCADE.violet} radius={[0, 0, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-[300px] flex items-center justify-center text-muted-foreground">{t('dashboard.noData')}</div>
          )}
        </CardContent>
      </Card>

      {/* By investor */}
      {stats?.investments_by_investor && stats.investments_by_investor.length > 0 && (
        <Card className="bezel">
          <CardHeader>
            <CardTitle className="text-xl font-heading flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-primary" /> {t('dashboard.byInvestor')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(() => {
              const COLORS = ['bg-primary', 'bg-secondary', 'bg-cyan-400', 'bg-violet-500', 'bg-emerald-400', 'bg-amber-500', 'bg-rose-500', 'bg-lime-400'];
              const maxVal = stats.investments_by_investor[0].total;
              const grand = stats.investments_by_investor.reduce((a, b) => a + b.total, 0);
              return (
                <div className="space-y-6">
                  <div className="space-y-3">
                    {stats.investments_by_investor.map((investor, index) => {
                      const percentage = (investor.total / maxVal) * 100;
                      const colorClass = COLORS[index % COLORS.length];
                      return (
                        <div key={index} className="space-y-1">
                          <div className="flex justify-between text-sm">
                            <span className="font-medium flex items-center gap-2">
                              <div className={`w-2 h-2 rounded-full ${colorClass}`} />
                              {investor._id || 'Unknown'}
                            </span>
                            <span className="text-muted-foreground font-mono tabular-nums">{formatCurrency(investor.total)}</span>
                          </div>
                          <div className="h-2.5 w-full bg-muted rounded-full overflow-hidden">
                            <div className={`h-full ${colorClass} transition-all duration-500 ease-in-out`} style={{ width: `${percentage}%` }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex flex-wrap gap-3 pt-4 border-t border-border">
                    {stats.investments_by_investor.map((investor, index) => (
                      <div key={index} className="flex items-center gap-2 text-sm bg-muted/50 px-2.5 py-1 rounded-md border border-border">
                        <div className={`w-3 h-3 rounded-full ${COLORS[index % COLORS.length]}`} />
                        <span className="font-medium">{investor._id || 'Unknown'}</span>
                        <span className="text-muted-foreground text-xs font-mono">({Math.round((investor.total / grand) * 100)}%)</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}
          </CardContent>
        </Card>
      )}

      {/* Recent operations */}
      <Card className="bezel" data-testid="recent-operations">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="eyebrow text-[10px] text-muted-foreground">{t('dashboard.recentOperations')}</CardTitle>
          <Button variant="ghost" size="sm" onClick={() => navigate('/operations')} data-testid="go-to-operations-btn">
            {t('common.view')} <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </CardHeader>
        <CardContent>
          {stats?.recent_operations && stats.recent_operations.length > 0 ? (
            <div className="space-y-2.5">
              {stats.recent_operations.map((op) => (
                <div key={op.id} className="flex items-center justify-between p-3 rounded-md bg-muted/40 border border-transparent hover:border-primary/30 hover:bg-muted/70 transition-colors">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-md bg-card flex items-center justify-center border border-border shrink-0">
                      <Receipt className="w-5 h-5 text-muted-foreground" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-sm truncate">{op.description}</p>
                      <p className="text-xs text-muted-foreground font-mono">{formatDate(op.date)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    {op.price && <span className="font-mono text-sm font-bold tabular-nums">{formatCurrency(op.price)}</span>}
                    <Badge className={`${getOperationColor(op.operation_type)} uppercase text-[10px] font-bold`}>
                      {t(`operations.${op.operation_type}`)}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground text-sm text-center py-8">{t('dashboard.noData')}</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;
